import React from 'react';
import { BOTTLE_ASPECT } from '../data/products';

/**
 * A Maison Vael bottle, rendered from the brand's product photography.
 *
 * Every SKU ships in the same bottle; only the juice colour differs, so the
 * image is chosen by `product.colourway`. Width is derived from the display
 * height and stamped on the element so the card never reflows as images land.
 *
 * The bottle is grounded rather than floated: it takes the top 91% of the
 * block, and the remaining 9% holds a contact shadow plus a gradient-masked
 * mirror reflection — all inside the same box, so callers' layouts are
 * untouched. The renders are bottom-aligned on a shared canvas, which is what
 * lets one shadow position work for all ten colourways.
 */
const BottleImage = ({ product, height, eager = false, className }) => {
  const width = Math.round(height * BOTTLE_ASPECT);
  const bottleH = Math.round(height * 0.91);
  const bottleW = Math.round(bottleH * BOTTLE_ASPECT);
  const base = `${process.env.PUBLIC_URL || ''}/bottles/${product.colourway}`;
  // Applied in the element's pre-transform space, then flipped with it by
  // scaleY(-1): "to top" here renders as strongest at the contact point.
  const reflectionMask =
    'linear-gradient(to top, rgba(0,0,0,0.30), rgba(0,0,0,0.08) 55%, transparent 100%)';

  return (
    <span
      className={className}
      style={{
        position: 'relative',
        display: 'block',
        width: `${width}px`,
        height: `${height}px`,
        overflow: 'hidden',
      }}
    >
      <img
        src={`${base}-440.webp`}
        srcSet={`${base}-220.webp 220w, ${base}-440.webp 440w`}
        sizes={`${bottleW}px`}
        width={bottleW}
        height={bottleH}
        alt={`${product.name} — ${product.type}, ${product.size}`}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={eager ? 'high' : 'auto'}
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: `${bottleW}px`,
          height: `${bottleH}px`,
          display: 'block',
          objectFit: 'contain',
          zIndex: 1,
        }}
      />
      {/* Contact shadow: a dark pool directly under the base line. */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '50%',
          top: `${bottleH}px`,
          transform: 'translate(-50%, -50%)',
          width: `${Math.round(bottleW * 0.92)}px`,
          height: `${Math.max(6, Math.round(height * 0.055))}px`,
          background:
            'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 45%, transparent 72%)',
          zIndex: 0,
        }}
      />
      {/* Mirror reflection: the same render flipped, fading out fast. */}
      <img
        src={`${base}-440.webp`}
        srcSet={`${base}-220.webp 220w, ${base}-440.webp 440w`}
        sizes={`${bottleW}px`}
        width={bottleW}
        height={bottleH}
        alt=""
        aria-hidden="true"
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        style={{
          position: 'absolute',
          top: `${bottleH}px`,
          left: '50%',
          transform: 'translateX(-50%) scaleY(-1)',
          width: `${bottleW}px`,
          height: `${bottleH}px`,
          display: 'block',
          objectFit: 'contain',
          opacity: 0.35,
          WebkitMaskImage: reflectionMask,
          maskImage: reflectionMask,
          zIndex: 0,
        }}
      />
    </span>
  );
};

export default BottleImage;
